import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI;

// True when at least one provider is configured — lets callers hide the whole
// "or continue with" section when social login isn't set up.
export const SOCIAL_AUTH_ENABLED = !!(GOOGLE_CLIENT_ID || APPLE_CLIENT_ID);

// Load a third-party <script> exactly once, resolving when ready.
function loadScript(src, id) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      if (existing.dataset.loaded === "true") return resolve();
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error(`Failed: ${src}`)));
      return undefined;
    }
    const s = document.createElement("script");
    s.src = src;
    s.id = id;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      s.dataset.loaded = "true";
      resolve();
    };
    s.onerror = () => reject(new Error(`Failed: ${src}`));
    document.head.appendChild(s);
    return undefined;
  });
}

function AppleGlyph() {
  return (
    <svg viewBox="0 0 384 512" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

/**
 * Google + Apple sign-in buttons. Renders only the providers that have a
 * client id configured. On success the user is signed in and routed to their
 * dashboard (advisors → /advisor).
 */
export default function SocialAuthButtons() {
  const { oauthLogin } = useAuth();
  const navigate = useNavigate();
  const googleBtnRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const finish = useCallback(
    async (provider, payload) => {
      setBusy(true);
      try {
        const u = await oauthLogin(provider, payload);
        toast.success("Signed in");
        navigate(u?.role === "pfa" ? "/advisor" : "/dashboard");
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setBusy(false);
      }
    },
    [oauthLogin, navigate],
  );

  // ---- Google Identity Services ----
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleBtnRef.current) return undefined;
    let cancelled = false;
    loadScript("https://accounts.google.com/gsi/client", "google-gsi")
      .then(() => {
        if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp) => finish("google", { credential: resp.credential }),
        });
        googleBtnRef.current.innerHTML = "";
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "pill",
          width: 320,
        });
      })
      .catch(() => {
        /* network / blocked — button just won't appear */
      });
    return () => {
      cancelled = true;
    };
  }, [finish]);

  // ---- Apple ----
  const appleSignIn = useCallback(async () => {
    if (!APPLE_CLIENT_ID) return;
    try {
      await loadScript(
        "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
        "apple-auth",
      );
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: APPLE_REDIRECT_URI || window.location.origin,
        usePopup: true,
      });
      const data = await window.AppleID.auth.signIn();
      const idToken = data?.authorization?.id_token;
      if (!idToken) throw new Error("no id_token");
      // Apple only sends the name on the very first authorization.
      let name;
      if (data.user?.name) {
        name = [data.user.name.firstName, data.user.name.lastName]
          .filter(Boolean)
          .join(" ");
      }
      await finish("apple", { credential: idToken, name });
    } catch (e) {
      if (e?.error === "popup_closed_by_user") return;
      toast.error("Apple sign-in was cancelled or failed");
    }
  }, [finish]);

  if (!SOCIAL_AUTH_ENABLED) return null;

  return (
    <div className="space-y-3">
      {GOOGLE_CLIENT_ID && (
        <div
          ref={googleBtnRef}
          data-testid="google-signin"
          className="flex justify-center [color-scheme:light]"
        />
      )}
      {APPLE_CLIENT_ID && (
        <button
          type="button"
          data-testid="apple-signin"
          onClick={appleSignIn}
          disabled={busy}
          className="w-full flex items-center justify-center gap-2 rounded-full bg-black text-white py-2.5 text-sm font-medium hover:bg-black/90 disabled:opacity-60 transition"
        >
          <AppleGlyph /> Continue with Apple
        </button>
      )}
    </div>
  );
}
